/**
 * Table of Contents — Interactivity API view module.
 *
 * Pure progressive enhancement. render.php already emits a working list of
 * fragment links, so everything here is an upgrade on top of markup that
 * navigates fine without it:
 *
 * - offset-aware scrolling, so a sticky header does not cover the heading
 * - focus moved to the destination, so keyboard users go where the page went
 * - scroll-spy highlighting via IntersectionObserver (no scroll listener)
 * - the collapse toggle
 *
 * Nothing runs on pages without the block.
 *
 * @package BrandyBlocks
 */

import { store, getContext, getElement } from '@wordpress/interactivity';

/**
 * Whether the visitor asked for less motion. Read per interaction rather than
 * cached, so a mid-session OS change is respected.
 *
 * @return {boolean} True when reduced motion is preferred.
 */
const prefersReducedMotion = () =>
	window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

/**
 * Resolve the heading a link points at.
 *
 * @param {HTMLElement} link Anchor element.
 * @return {HTMLElement|null} Target heading, or null when it is missing.
 */
const getTarget = ( link ) => {
	const id = link?.dataset?.brandyTocTarget;
	if ( ! id ) {
		return null;
	}

	// getElementById rather than a #id selector: heading ids are derived from
	// author text and can start with a digit or contain characters that are
	// not valid in a CSS selector.
	return document.getElementById( id );
};

/**
 * Move keyboard focus to the heading the page just scrolled to.
 *
 * Headings are not focusable, so one is made focusable just in time and the
 * attribute removed again on blur — leaving tabindex behind would insert every
 * heading into the tab order.
 *
 * @param {HTMLElement} heading Destination heading.
 */
const focusHeading = ( heading ) => {
	if ( ! heading.hasAttribute( 'tabindex' ) ) {
		heading.setAttribute( 'tabindex', '-1' );
		heading.addEventListener(
			'blur',
			() => heading.removeAttribute( 'tabindex' ),
			{ once: true }
		);
	}

	heading.focus( { preventScroll: true } );
};

store( 'brandy/table-of-contents', {
	state: {
		/**
		 * Whether this link points at the heading currently in view. Read as a
		 * derived value by data-wp-class--is-active on every link.
		 *
		 * @return {boolean} True when active.
		 */
		get isActive() {
			const { ref } = getElement();
			const ctx = getContext();
			return (
				!! ctx.activeAnchor &&
				ref?.dataset?.brandyTocTarget === ctx.activeAnchor
			);
		},

	},

	actions: {
		toggle() {
			const ctx = getContext();
			ctx.collapsed = ! ctx.collapsed;
		},

		navigate( event ) {
			const ctx = getContext();
			const { ref } = getElement();
			const heading = getTarget( ref );

			// A missing heading means the anchor never made it into the page.
			// Leave the default fragment jump alone rather than swallowing the
			// click and going nowhere.
			if ( ! heading ) {
				return;
			}

			event.preventDefault();

			const top =
				heading.getBoundingClientRect().top +
				window.scrollY -
				( ctx.scrollOffset || 0 );

			window.scrollTo( {
				top: Math.max( 0, top ),
				behavior:
					ctx.smoothScroll && ! prefersReducedMotion()
						? 'smooth'
						: 'auto',
			} );

			// Reflect the destination immediately; the observer would other-
			// wise only catch up once the smooth scroll finishes.
			ctx.activeAnchor = heading.id;

			focusHeading( heading );

			// Keep the address bar in step without triggering a second jump.
			if ( window.history?.replaceState ) {
				window.history.replaceState( null, '', `#${ heading.id }` );
			}
		},
	},

	callbacks: {
		init() {
			const ctx = getContext();
			const { ref } = getElement();

			if ( ! ctx.scrollSpy ) {
				return;
			}

			const links = Array.from(
				ref.querySelectorAll( '[data-brandy-toc-target]' )
			);
			const headings = links.map( getTarget ).filter( Boolean );

			if ( ! headings.length ) {
				return;
			}

			// Track which headings are on screen and highlight the topmost of
			// them. The bottom margin keeps a heading from counting while it is
			// still in the lower part of the viewport, which is what makes the
			// highlight follow reading position rather than run ahead of it.
			const visible = new Set();

			const observer = new IntersectionObserver(
				( entries ) => {
					entries.forEach( ( entry ) => {
						if ( entry.isIntersecting ) {
							visible.add( entry.target );
						} else {
							visible.delete( entry.target );
						}
					} );

					if ( ! visible.size ) {
						return;
					}

					// Document order is the reading order; take the first one
					// still on screen.
					const current = headings.find( ( heading ) =>
						visible.has( heading )
					);

					if ( current ) {
						ctx.activeAnchor = current.id;
					}
				},
				{
					rootMargin: `-${ ctx.scrollOffset || 0 }px 0px -65% 0px`,
					threshold: 0,
				}
			);

			headings.forEach( ( heading ) => observer.observe( heading ) );

			// data-wp-init's return value is the cleanup, run if the block is
			// ever removed from the DOM.
			return () => observer.disconnect();
		},
	},
} );
